import { api, type DirEntry, type ExifInfo, type IsiZip } from '../../lib/api'

/**
 * Sumber berkas.
 *
 * Aplikasi lama punya TIGA penjelajah berkas dengan kemampuan yang timpang:
 * File Saya punya seret-lepas tetapi klik kanannya hanya bekerja di tampilan
 * kisi; halaman share bisa pratinjau tetapi kehilangan seleksi setiap pindah
 * folder dan tidak bisa mengunduh satu folder; File Manager admin tidak
 * bereaksi sama sekali ketika berkas diklik. Duplikasi itu bahkan turun ke
 * backend berupa lima pasang endpoint kembar.
 *
 * Di sini penjelajahnya satu, dan perbedaan penyimpanan diringkas menjadi
 * adaptor kecil ini. Konsekuensi langsungnya: pengunjung share link akhirnya
 * bisa mengunduh seluruh folder, dan admin akhirnya punya operasi berkas.
 */

export interface Kemampuan {
  bacaSaja: boolean
  bolehPilih: boolean
  bolehUnduh: boolean
  bolehZip: boolean
  bolehUnggah: boolean
  /** Ganti nama, pindah, salin, hapus, folder baru. */
  bolehUbah: boolean
  bolehBagikan: boolean
  bolehBintang: boolean
}

export const TANPA_KEMAMPUAN: Kemampuan = {
  bacaSaja: true,
  bolehPilih: false,
  bolehUnduh: false,
  bolehZip: false,
  bolehUnggah: false,
  bolehUbah: false,
  bolehBagikan: false,
  bolehBintang: false,
}

export interface SumberBerkas {
  /** Pengenal unik, dipakai sebagai kunci penyimpanan preferensi. */
  id: string
  /** Nama akar yang tampil di remah roti, misalnya "Ruang Saya". */
  labelAkar: string
  kemampuan: Kemampuan

  daftar(dir: string, signal?: AbortSignal): Promise<DirEntry[]>
  urlUnduh(path: string): string
  urlLihat(path: string): string
  urlThumbnail(path: string, ukuran?: number): string
  urlPratinjau(path: string, lebar?: number, tinggi?: number): string
  /** Unduhan HEIF yang sudah dikonversi ke JPEG; null bila tidak didukung. */
  urlJpeg?(path: string): string
  exif(path: string, signal?: AbortSignal): Promise<ExifInfo>
  unduhZip(paths: string[]): void
  /** Daftar isi arsip ZIP tanpa mengekstraknya. */
  isiZip(path: string, signal?: AbortSignal): Promise<IsiZip[]>
  /** Toggle bintang; null bila sumbernya tidak mendukung. */
  toggleBintang?(item: DirEntry): Promise<boolean>
}

/** Berkas di dalam sebuah share link. */
export function sumberShare(token: string, tulis: boolean, masuk: boolean): SumberBerkas {
  return {
    id: `share:${token}`,
    labelAkar: 'Folder dibagikan',
    kemampuan: {
      bacaSaja: !tulis,
      bolehPilih: true,
      bolehUnduh: true,
      bolehZip: true,
      bolehUnggah: tulis && masuk,
      bolehUbah: false,
      bolehBagikan: false,
      // Membintangi menyimpan ke akun, jadi perlu sudah masuk.
      bolehBintang: masuk,
    },
    daftar: async (dir, signal) => (await api.share.list(token, dir, signal)).items,
    urlUnduh: (p) => api.share.fileUrl(token, p),
    urlLihat: (p) => api.share.viewUrl(token, p),
    urlThumbnail: (p, s) => api.share.thumbUrl(token, p, s),
    urlPratinjau: (p, w, h) => api.share.previewUrl(token, p, w, h),
    urlJpeg: (p) => api.share.jpegUrl(token, p),
    exif: (p, signal) => api.share.exif(token, p, signal),
    unduhZip: (paths) => api.share.zip(token, paths),
    isiZip: async (p, signal) => (await api.share.zipInfo(token, p, signal)).entries,
  }
}

/** Ruang berkas pribadi pengguna. */
export function sumberPribadi(): SumberBerkas {
  return {
    id: 'personal',
    labelAkar: 'Ruang Saya',
    kemampuan: {
      bacaSaja: false,
      bolehPilih: true,
      bolehUnduh: true,
      bolehZip: true,
      bolehUnggah: true,
      bolehUbah: true,
      bolehBagikan: true,
      bolehBintang: true,
    },
    daftar: async (dir, signal) => {
      const r = await api.userdata.list(dir, signal)
      // Bentuk item userdata berbeda dari DirEntry; disamakan di sini supaya
      // penjelajahnya tidak perlu tahu dari sumber mana datanya berasal.
      return r.items.map((i) => ({
        name: i.name,
        path: dir ? `${dir}/${i.name}` : i.name,
        is_dir: i.is_dir,
        size: i.size,
        modified: i.modified,
        ext: i.ext,
        mime: i.mime,
      }))
    },
    urlUnduh: (p) => api.userdata.serveUrl(p, true),
    urlLihat: (p) => api.userdata.serveUrl(p, false),
    urlThumbnail: (p, s) => api.userdata.thumbUrl(p, s),
    urlPratinjau: (p, w, h) => api.userdata.previewUrl(p, w, h),
    urlJpeg: (p) => api.userdata.jpegUrl(p),
    exif: (p, signal) => api.userdata.exif(p, signal),
    isiZip: async (p, signal) => (await api.userdata.zipInfo(p, signal)).entries,
    toggleBintang: async (item) => (await api.stars.toggleBerkas(item.path, item.name)).starred,
    unduhZip: (paths) => {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = '/api/v1/userdata/zip'
      form.style.display = 'none'
      const f = document.createElement('input')
      f.type = 'hidden'
      f.name = 'payload'
      f.value = JSON.stringify({ paths })
      form.appendChild(f)
      document.body.appendChild(form)
      form.submit()
      setTimeout(() => form.remove(), 1000)
    },
  }
}

/** NAS bersama — hanya untuk admin, sementara ini baca saja. */
export function sumberNas(): SumberBerkas {
  return {
    id: 'nas',
    labelAkar: 'NAS Bersama',
    kemampuan: {
      bacaSaja: true,
      bolehPilih: true,
      bolehUnduh: false,
      bolehZip: false,
      bolehUnggah: true,
      bolehUbah: false,
      bolehBagikan: true,
      bolehBintang: false,
    },
    daftar: async (dir, signal) => (await api.files.list(dir, signal)).items,
    urlUnduh: () => '#',
    urlLihat: () => '#',
    urlThumbnail: () => '',
    urlPratinjau: () => '',
    exif: async () => ({}),
    isiZip: async () => [],
    unduhZip: () => {},
  }
}

// ── Pengurutan ─────────────────────────────────────────────────────────────

export type KolomUrut = 'nama' | 'ukuran' | 'tanggal' | 'tipe'
export type ArahUrut = 'naik' | 'turun'

/**
 * Urutkan isi direktori.
 *
 * Folder SELALU di atas berkas, apa pun kolom dan arah yang dipilih —
 * perbandingan is_dir dilakukan sebelum perbandingan nilai. Ini aturan lama
 * yang paling mudah hilang saat ditulis ulang dan paling cepat disadari
 * pengguna kalau sampai hilang.
 */
export function urutkan(items: DirEntry[], kolom: KolomUrut, arah: ArahUrut): DirEntry[] {
  const faktor = arah === 'naik' ? 1 : -1

  return [...items].sort((a, b) => {
    if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1

    let hasil = 0
    switch (kolom) {
      case 'ukuran':
        hasil = (a.size ?? 0) - (b.size ?? 0)
        break
      case 'tanggal':
        hasil = a.modified - b.modified
        break
      case 'tipe':
        hasil = (a.ext ?? '').localeCompare(b.ext ?? '', 'id')
        break
      default:
        hasil = 0
    }

    // Nama selalu jadi pemutus akhir, agar urutannya stabil dan tidak
    // berubah-ubah untuk berkas yang nilainya sama.
    if (hasil === 0) {
      return (
        faktor * a.name.localeCompare(b.name, 'id', { sensitivity: 'base', numeric: true })
      )
    }
    return faktor * hasil
  })
}

/**
 * Keluarga HEIF. Browser tidak bisa menampilkannya sendiri kecuali Safari,
 * tetapi pratinjau memang memakai versi webp bikinan server, jadi ketiganya
 * tetap aman diperlakukan sebagai gambar di sini.
 */
export const EXT_HEIF = new Set(['hif', 'heic', 'heif'])

const EXT_GAMBAR = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'avif', 'tif', 'tiff',
  ...EXT_HEIF,
])

/**
 * Bisa diminta dalam bentuk JPEG?
 *
 * .HIF dari kamera Sony tidak bisa dibuka Photoshop versi lama, jadi berkas
 * keluarga HEIF diberi jalur unduhan kedua yang sudah dikonversi.
 */
export function bisaUnduhJpeg(item: DirEntry): boolean {
  return !item.is_dir && EXT_HEIF.has((item.ext ?? '').toLowerCase())
}
const EXT_VIDEO = new Set(['mp4', 'mkv', 'mov', 'avi', 'webm', 'm4v'])
const EXT_AUDIO = new Set(['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'])
const EXT_TEKS = new Set([
  'txt', 'md', 'csv', 'log', 'json', 'xml', 'html', 'htm', 'css', 'js', 'ts',
  'py', 'sh', 'yaml', 'yml', 'ini', 'conf', 'toml', 'sql', 'php',
])

export type JenisPratinjau = 'gambar' | 'video' | 'audio' | 'pdf' | 'teks' | 'zip' | 'tidak'

export function jenisPratinjau(item: DirEntry): JenisPratinjau {
  if (item.is_dir) return 'tidak'
  const ext = (item.ext ?? '').toLowerCase()
  if (EXT_GAMBAR.has(ext)) return 'gambar'
  if (EXT_VIDEO.has(ext)) return 'video'
  if (EXT_AUDIO.has(ext)) return 'audio'
  if (ext === 'pdf') return 'pdf'
  if (ext === 'zip') return 'zip'
  if (EXT_TEKS.has(ext)) return 'teks'
  return 'tidak'
}

export function bisaDipratinjau(item: DirEntry): boolean {
  return jenisPratinjau(item) !== 'tidak'
}

/**
 * Tebak tampilan awal: kisi bila mayoritas isinya gambar.
 *
 * Detail kecil dari versi lama yang sangat terasa — membuka folder foto lalu
 * mendapati daftar teks memaksa pengguna mengganti tampilan setiap kali.
 */
export function tampilanTerbaik(items: DirEntry[]): 'daftar' | 'kisi' {
  const berkas = items.filter((i) => !i.is_dir)
  if (berkas.length === 0) return 'daftar'
  const gambar = berkas.filter((i) => EXT_GAMBAR.has((i.ext ?? '').toLowerCase())).length
  return gambar / berkas.length >= 0.5 ? 'kisi' : 'daftar'
}

/**
 * Ekstensi yang dapat dibuka editor teks.
 *
 * Daftar ini WAJIB menjadi satu sumber kebenaran yang dipakai editor,
 * penjelajah berkas, dan endpoint pembuatan berkas — di versi lama daftarnya
 * ditulis ulang di tiga tempat dan sudah mulai berbeda isinya.
 */
export const EXT_EDITABEL = new Set([
  'txt', 'md', 'json', 'xml', 'csv', 'js', 'ts', 'jsx', 'tsx', 'mjs',
  'php', 'html', 'htm', 'css', 'scss', 'less', 'py', 'sh', 'bash', 'zsh', 'fish',
  'yaml', 'yml', 'toml', 'ini', 'conf', 'env', 'sql', 'log', 'bat', 'ps1',
  'c', 'cpp', 'h', 'hpp', 'java', 'rb', 'go', 'rs', 'swift', 'kt', 'dart',
])
